import React, { useState, useEffect } from 'react';
import { Users, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { healthService } from '../services/healthService';

const Connections = () => {
  const [connectionStats, setConnectionStats] = useState(null);
  const [connectionDetails, setConnectionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConnectionData();
    // Refresh data every 10 seconds as mentioned in the UI
    const interval = setInterval(fetchConnectionData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchConnectionData = async () => {
    try {
      setError(null);
      
      // Fetch connections first (this endpoint works without auth issues)
      const details = await healthService.getConnections();
      console.log('✅ Connection Details:', details);
      setConnectionDetails(details);
      
      // Try to fetch stats, but don't fail if it errors
      try {
        const stats = await healthService.getConnectionStats();
        console.log('✅ Connection Stats:', stats);
        setConnectionStats(stats);
      } catch (statsError) {
        console.warn('⚠️ Could not fetch connection stats (will use fallback):', statsError.message);
        // Use connection details data as fallback for stats
        setConnectionStats({
          connection_stats: {
            total_connections: 0,
            active_connections: details?.active_connections || 0,
            total_ever_connected: 0,
            total_bytes_received: 0
          }
        });
      }
    } catch (err) {
      console.error('❌ Error fetching connection data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="lg" text="Loading connections..." />
      </div>
    );
  }

  const totalConnections = connectionStats?.connection_stats?.total_ever_connected || 0;
  const activeConnections = connectionStats?.connection_stats?.active_connections || 0;
  // Ensure completed connections is never negative (handles server restart case)
  const completedConnections = Math.max(0, totalConnections - activeConnections);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div className="container" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        {/* Header */}
        <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="dashboard-title">Active Connections</h1>
            <p className="dashboard-subtitle">Monitor and manage active WebSocket connections</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={fetchConnectionData}
              className="btn btn-secondary"
            >
              <RefreshCw style={{ width: '16px', height: '16px' }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-container">
            <div className="error-title">Error</div>
            <div className="error-message">
              {typeof error === 'string' 
                ? error 
                : error.message || JSON.stringify(error) || 'Failed to load connection data'}
            </div>
          </div>
        )}

        {/* Connection Stats */}
        <div className="grid grid-cols-3" style={{ marginBottom: '32px' }}>
          <div className="stats-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <div>
                <div className="stats-label">Total Connections</div>
                <div className="stats-number">{totalConnections}</div>
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'white'
                }}></div>
              </div>
              <div>
                <div className="stats-label">Active (Not Terminated)</div>
                <div className="stats-number">{activeConnections}</div>
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: '#8b5cf6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '16px',
                  height: '2px',
                  background: 'white'
                }}></div>
              </div>
              <div>
                <div className="stats-label">Completed</div>
                <div className="stats-number">{completedConnections}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Details */}
        <div className="table-container">
          <div className="table-header">
            <h3 className="table-title">Connection Details</h3>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              {connectionDetails?.connections?.length || 0} connection(s) (including recent history)
            </div>
          </div>
          
          {connectionDetails?.connections && connectionDetails.connections.length > 0 ? (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Connection ID</th>
                    <th>Status</th>
                    <th>Company</th>
                    <th>Session ID</th>
                    <th>Connected Since</th>
                    <th>Duration</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {connectionDetails.connections.map((connection, index) => {
                    // Calculate duration
                    const calculateDuration = (connectedSince) => {
                      try {
                        const start = new Date(connectedSince * 1000); // Unix timestamp to Date
                        const now = new Date();
                        const diffMs = now - start;
                        const diffSecs = Math.floor(diffMs / 1000);
                        const hours = Math.floor(diffSecs / 3600);
                        const minutes = Math.floor((diffSecs % 3600) / 60);
                        const seconds = diffSecs % 60;
                        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                      } catch {
                        return '0:00:00';
                      }
                    };

                    // Format timestamp
                    const formatTimestamp = (timestamp) => {
                      try {
                        return new Date(timestamp * 1000).toLocaleString();
                      } catch {
                        return 'N/A';
                      }
                    };

                    // Determine status
                    const status = connection.is_terminated ? 'terminated' : 'active';
                    const statusClass = status === 'active' ? 'status-success' : 'status-warning';

                    return (
                      <tr key={connection.connection_id || index}>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {connection.connection_id ? connection.connection_id.substring(0, 8) : `conn_${index + 1}`}...
                        </td>
                        <td>
                          <span className={`status-badge ${statusClass}`}>
                            {status}
                          </span>
                        </td>
                        <td style={{ fontWeight: '500' }}>
                          {connection.company_name || 'Unknown'}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '11px', color: '#64748b' }}>
                          {connection.session_id || 'N/A'}
                        </td>
                        <td>{formatTimestamp(connection.connected_since)}</td>
                        <td style={{ fontFamily: 'monospace', fontWeight: '500' }}>
                          {calculateDuration(connection.connected_since)}
                        </td>
                        <td>{formatTimestamp(connection.last_received)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ 
              padding: '48px', 
              textAlign: 'center', 
              color: '#64748b',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Users style={{ width: '48px', height: '48px', color: '#cbd5e1' }} />
              <div>
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>No active connections</div>
                <div style={{ fontSize: '14px', marginBottom: '8px' }}>No WebSocket connections are currently active.</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                  To test: Open a WebSocket connection to ws://localhost:8002/ws/your_company
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Connection Monitoring Info */}
        <div className="info-box">
          <div className="info-title">
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'white'
              }}></div>
            </div>
            Connection Monitoring
          </div>
          <ul className="info-list">
            <li>Connections are automatically refreshed every 10 seconds</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Connections;